import { useContext, useMemo } from "react";
import { useAtomValue } from "jotai";

import { ModuleBuilderContext } from "@src/features/libraryItem/module/contexts/ModuleBuilderContext";
import { CourseBuilderContext } from "@src/features/libraryItem/course/contexts/CourseBuilderContext";
import useSkillBuilderStore from "@src/features/libraryItem/skill/hooks/useSkillBuilderStore";
import { ActiveClientFragment } from "@src/lib/ui/ActiveClientsAvatarStack.generated";
import { ActiveClientsInput } from "@src/types.generated";
import useActiveClientsCore from "@src/features/builders/shared/presence/useActiveClientsCore";

import { moduleVersionIdAtom } from "../module/atoms";
import { courseVersionIdAtom } from "../course/atoms";
import { richTextDocumentIdAtom } from "../resource/atoms";

type UseActiveClients = {
  activeClients: ActiveClientFragment[];
};

// LibraryItem wrapper: assembles the active-clients input from the
// builder contexts + version atoms and delegates to useActiveClientsCore.
const useActiveClients = (): UseActiveClients => {
  const courseBuilderContext = useContext(CourseBuilderContext);
  const moduleBuilderContext = useContext(ModuleBuilderContext);
  const skillVersionId = useSkillBuilderStore(
    (state) => state.skillVersion?.id,
  );
  const moduleVersionId = useAtomValue(moduleVersionIdAtom);
  const courseVersionId = useAtomValue(courseVersionIdAtom);
  const richTextDocumentId = useAtomValue(richTextDocumentIdAtom);

  const input = useMemo<ActiveClientsInput | null>(() => {
    if (!moduleBuilderContext && !courseBuilderContext && !richTextDocumentId) {
      return null;
    }
    return {
      courseVersionId: courseVersionId,
      moduleVersionId: moduleVersionId,
      skillVersionId,
      richTextDocumentId: richTextDocumentId,
    };
  }, [
    courseBuilderContext,
    moduleBuilderContext,
    courseVersionId,
    moduleVersionId,
    skillVersionId,
    richTextDocumentId,
  ]);

  const activeClients = useActiveClientsCore(input);
  return useMemo(() => ({ activeClients }), [activeClients]);
};

export default useActiveClients;
